import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '@/lib/config';
import { AuthFormCard } from '@/components/AuthFormCard';
import './ActivateInvitation.css';

const ConfirmClassInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';
  const [error, setError] = useState<string | null>(token ? null : 'Invalid invitation.');
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }

    const validateToken = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/validate-invitation?token=${encodeURIComponent(token)}`);
        if (!response.ok) {
          navigate('/');
          return;
        }
        const data: { requiresRegistration: boolean } = await response.json();
        if (data.requiresRegistration) {
          navigate(`/activate?token=${encodeURIComponent(token)}`);
        }
      } catch {
        navigate('/');
      }
    };

    validateToken();
  }, [token, navigate]);

  const handleConfirm = async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/confirm-class-invitation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!response.ok) throw new Error(await response.text() || 'Invitation could not be confirmed.');
      setCompleted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invitation could not be confirmed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthFormCard title="Confirm invitation">
      {completed ? (
        <>
          <p>You have been added to the class. You can now log in.</p>
          <Link className="auth-form-link" to="/">Back to login</Link>
        </>
      ) : (
        <div className="auth-form compact">
          <p>You have been invited to a class. Would you like to join?</p>

          {error && <div className="auth-form-error">{error}</div>}

          <button type="button" className="auth-form-button" disabled={loading || !token} onClick={() => void handleConfirm()}>
            {loading ? 'Confirming...' : 'Confirm invitation'}
          </button>
        </div>
      )}
    </AuthFormCard>
  );
};

export default ConfirmClassInvitation;
